import React, { useEffect, useState } from 'react'
import { Alert, Button, Modal, Spin } from 'antd'
import type { Member, Organization, OrgRole, OrgSelection } from '../../types'
import { canAddUsersToOrganization, existingProtectedRoles, protectedRolesFromList } from '../../utils'
import NoticeBanner from '../shared/NoticeBanner'
import OrgRolePicker from '../shared/OrgRolePicker'

interface AssignOrgModalProps {
  open: boolean
  member: Member | null
  organizations: Organization[]
  selections: Record<string, OrgSelection>
  loading?: boolean
  submitting?: boolean
  currentCustomerId?: string | null
  onSelectionChange: (orgId: string, patch: Partial<OrgSelection>) => void
  onCancel: () => void
  onSubmit: () => void
  onUpgrade?: (organization: Organization) => void
  existingRolesByOrgId?: Record<string, OrgRole[]>
}

const AssignOrgModal: React.FC<AssignOrgModalProps> = ({
  open,
  member,
  organizations,
  selections,
  loading = false,
  submitting = false,
  currentCustomerId,
  onSelectionChange,
  onCancel,
  onSubmit,
  onUpgrade,
  existingRolesByOrgId = {},
}) => {
  const canAddToAnyOrg = organizations.some(canAddUsersToOrganization)
  const [accessBaseline, setAccessBaseline] = useState<Record<string, boolean> | null>(null)
  const [invalidRoleOrgIds, setInvalidRoleOrgIds] = useState<string[]>([])
  const [warning, setWarning] = useState('')

  useEffect(() => {
    if (!open) {
      setInvalidRoleOrgIds([])
      setWarning('')
      setAccessBaseline(null)
      return
    }
    if (loading) {
      setAccessBaseline(null)
      return
    }
    setAccessBaseline(Object.fromEntries(
      organizations.map((org) => [org.id, !!selections[org.id]?.checked]),
    ))
    // Capture saved access when loading finishes; later checkbox edits must not reset it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, open])

  const handleSave = () => {
    const missingRoleSelections = Object.entries(selections).filter(([, selection]) => (
      selection.checked && selection.roles.length === 0
    ))
    const missingRoleOrgs = missingRoleSelections
      .map(([orgId]) => organizations.find((item) => item.id === orgId)?.name || orgId)
    if (missingRoleOrgs.length > 0) {
      setInvalidRoleOrgIds(missingRoleSelections.map(([orgId]) => orgId))
      setWarning(`Select at least one role for ${missingRoleOrgs.join(', ')}.`)
      return
    }
    setInvalidRoleOrgIds([])
    setWarning('')
    onSubmit()
  }

  return (
  <Modal
    open={open}
    title="Manage organization access"
    onCancel={onCancel}
    footer={[
      <Button key="cancel" onClick={onCancel}>
        Cancel
      </Button>,
      <Button key="submit" type="primary" loading={submitting} disabled={loading} onClick={handleSave}>
        Save changes
      </Button>,
    ]}
    width={640}
    className="um-refactor__modal um-refactor__modal--assign"
    destroyOnClose
  >
    {member && (
      <p className="um-refactor__modal-desc">
        Choose which of your organizations <b>{member.name}</b> belongs to. The role is set independently per organization.
      </p>
    )}
    <Spin spinning={loading} wrapperClassName="um-refactor__picker-spin">
      <OrgRolePicker
        organizations={organizations}
        selections={selections}
        restrictAdds
        upgradeIntent="add"
        currentCustomerId={currentCustomerId}
        onUpgrade={onUpgrade}
        allowEmptyRoles
        invalidRoleOrgIds={invalidRoleOrgIds}
        onChange={(orgId, patch) => {
          setWarning('')
          setInvalidRoleOrgIds((ids) => ids.filter((id) => id !== orgId))
          onSelectionChange(orgId, patch)
        }}
        accessBaselineByOrgId={accessBaseline ?? undefined}
        lockedRolesByOrgId={Object.fromEntries(
          organizations.map((org) => [
            org.id,
            existingRolesByOrgId[org.id]
              ? protectedRolesFromList(existingRolesByOrgId[org.id])
              : existingProtectedRoles(member, org.id),
          ]),
        )}
      />
    </Spin>
    {warning && (
      <Alert
        className="um-refactor__notice um-refactor__notice--warn"
        type="warning"
        message={<span style={{ whiteSpace: 'pre-line' }}>{warning}</span>}
        showIcon
      />
    )}
    {!canAddToAnyOrg && (
      <NoticeBanner variant="info">
        Users can only be added to Business or Enterprise organizations. You can still remove existing access or update roles where this person already belongs.
      </NoticeBanner>
    )}
    <NoticeBanner variant="info">
      Role changes take effect after the user signs in again.
    </NoticeBanner>
    <NoticeBanner variant="warn">
      Changes are applied directly. Removing an organization disables access there; adding a disabled organization enables it again.
    </NoticeBanner>
  </Modal>
  )
}

export default AssignOrgModal
